import React, {useRef} from "react";

//@ts-ignore
import Ring from "ringjs";

import {useEffect, useState, useMemo} from "react";
import {Card, CardBody, Col, Row, Table} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import PageHeader from "../components/Common/PageHeader";
import HelpIcon from "../components/Help/HelpIcon";
import {Scatter} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";

import colors from "../styles/colors";
import {useChartTheme, ChartThemeProvider} from "../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../components/Charts/ChartErrorBoundary";
import {chartHeight} from "../constants";

import Ping from "../utils/ping";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend,
  TimeScale,
  annotationPlugin
);

const chartWindowMilliseconds: number = 3 * 60 * 1000;
const baseRate: number = 1000;
const pinger = new Ping();
const badThreshold: number = 250;
let pingCount: number = 0;

const PingTestContent = () => {
  const [realtimeData, setRealtimeData] = useState(new Ring(300));
  const realtimeDataRef = useRef(realtimeData);
  const [currentTime, setCurrentTime] = useState(Date.now()); // For chart animation
  const chartRef = useRef<ChartJS<"scatter", {x: number; y: number}[], unknown>>(null);

  const time: any = useRef(new Date());

  useEffect(() => {
    const getPingTimeAsync = (): Promise<number> => {
      return new Promise((resolve: any, reject: any) => {
        // timeout after 5 seconds to prevent hanging
        const timer = setTimeout(() => {
          reject(new Error("Promise timed out after 5000 ms"));
        }, 5000);

        const randomString = Math.floor(Math.random() * 0xffffffffffff).toString(36);
        pinger.ping(
          `https://rtc.connect-telecom.us-west-2.amazonaws.com/ping?x=${randomString}`,
          (err: any, response: number) => {
            if (err !== null) {
              clearTimeout(timer);
              reject(err);
            } else {
              clearTimeout(timer);
              resolve(response);
            }
          }
        );
      });
    };

    const getNewEvent = async (t: any) => {
      let response: number = 0;
      try {
        response = (await getPingTimeAsync()) as number;
        return {time: t, value: response};
      } catch {
        return undefined;
      }
    };

    let interval: any = null;

    interval = setInterval(async () => {
      const t: any = new Date(time.current.getTime() + baseRate);

      time.current = t;

      const event: any = await getNewEvent(t);

      if (event !== undefined) {
        pingCount += 1;
        if (pingCount > 1) {
          realtimeDataRef.current.push(event);
          // Create a new Ring instance to trigger React re-render
          const newRing = new Ring(300);
          realtimeDataRef.current.toArray().forEach((item: any) => newRing.push(item));
          realtimeDataRef.current = newRing;
          setRealtimeData(newRing);
        }
      }
    }, baseRate);

    return function cleanup() {
      clearInterval(interval);
    };
  }, []); // Remove realtimeData dependency to prevent interval recreation

  // Animation timer for smooth chart scrolling
  useEffect(() => {
    const animationInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 500); // Update every 500ms for smoother animation

    return () => clearInterval(animationInterval);
  }, []);

  const latestTime: string = `${time.current}`;

  // Calculate chart data and statistics with real-time window
  const {chartData, maxPing, avgPing, chartTimeRange} = useMemo(() => {
    let dataArray = [];

    try {
      // Safely extract data from Ring buffer
      if (realtimeData && typeof realtimeData.toArray === "function") {
        dataArray = realtimeData.toArray() || [];
      } else if (realtimeData && Array.isArray(realtimeData)) {
        dataArray = realtimeData;
      }
    } catch {
      dataArray = [];
    }

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return {
        chartData: [],
        maxPing: 0,
        avgPing: 0,
        chartTimeRange: {start: currentTime - chartWindowMilliseconds, end: currentTime}
      };
    }

    // Create a moving time window - chart shows last 3 minutes from current time
    const windowStart = currentTime - chartWindowMilliseconds;
    const windowEnd = currentTime;

    const filteredData = dataArray
      .filter((item: any) => {
        try {
          return (
            item &&
            item.time &&
            typeof item.time.getTime === "function" &&
            item.time.getTime() >= windowStart &&
            item.time.getTime() <= windowEnd
          );
        } catch {
          return false;
        }
      })
      .map((item: any) => {
        try {
          return {
            timestamp: item.time.getTime(),
            time: item.time.getTime(),
            ping: item.value,
            formattedTime: item.time.toLocaleTimeString()
          };
        } catch {
          return null;
        }
      })
      .filter((item: any) => item !== null)
      .sort((a: any, b: any) => a.timestamp - b.timestamp);

    // Add empty data points at the current time to extend the line to the right edge
    if (filteredData.length > 0) {
      const lastDataPoint = filteredData[filteredData.length - 1];
      const timeSinceLastPoint = currentTime - (lastDataPoint?.timestamp || 0);

      // If the last data point is more than 10 seconds old, add a gap
      if (timeSinceLastPoint > 10000) {
        filteredData.push({
          timestamp: currentTime,
          time: currentTime,
          ping: null, // null will create a gap in the line
          formattedTime: new Date(currentTime).toLocaleTimeString()
        });
      }
    }

    // Calculate statistics from all data points (not just filtered)
    let allPingValues = [];
    let maxVal = 0;
    let avgVal = 0;

    try {
      allPingValues = dataArray
        .filter((item: any) => item && typeof item.value === "number" && !isNaN(item.value))
        .map((item: any) => item.value);

      if (allPingValues.length > 0) {
        maxVal = Math.max(...allPingValues);
        avgVal = Math.round(allPingValues.reduce((a: number, b: number) => a + b, 0) / allPingValues.length);
      }
    } catch {
      // Error calculating statistics
    }

    return {
      chartData: filteredData,
      maxPing: Math.round(maxVal),
      avgPing: avgVal,
      chartTimeRange: {start: windowStart, end: windowEnd}
    };
  }, [realtimeData, currentTime]);

  // Use chart theme
  const {getThemeColors} = useChartTheme();
  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    return chartData
      .filter((d) => d && d.ping !== null && d.ping !== undefined)
      .map((d) => ({
        x: d!.time,
        y: d!.ping
      }));
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"scatter", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "Ping",
        data: preparedChartData,
        backgroundColor: colors.brand,
        borderColor: colors.brand,
        pointRadius: 2,
        pointHoverRadius: 4,
        showLine: false // Scatter plot, no lines
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"scatter"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations for real-time updates
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const value = context.parsed?.y;
              return value !== null && value !== undefined ? `Ping: ${Math.round(value)}ms` : "";
            }
          }
        },
        annotation: {
          annotations: {
            // // Bad threshold line
            // badThresholdLine: {
            //   type: "line" as const,
            //   scaleID: "y",
            //   value: badThreshold,
            //   borderColor: colors.fiestared,
            //   borderWidth: 2,
            //   borderDash: [5, 5],
            //   label: {
            //     display: true,
            //     content: "500ms",
            //     position: "end" as const,
            //     backgroundColor: colors.fiestared,
            //     color: "white",
            //     font: {
            //       size: 12,
            //       weight: "bold" as const,
            //     },
            //     padding: 4
            //   }
            // },
            // Average line
            ...(avgPing > 0
              ? {
                  avgLine: {
                    type: "line" as const,
                    scaleID: "y",
                    value: avgPing,
                    borderColor: colors.yellow,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    label: {
                      display: true,
                      content: `Avg: ${avgPing}ms`,
                      position: "start" as const,
                      backgroundColor: colors.yellow,
                      color: "black",
                      font: {
                        size: 12,
                        weight: "bold" as const
                      },
                      padding: 4
                    }
                  }
                }
              : {})
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 10,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return new Date(value).toLocaleTimeString("en-US", {
                hour12: false,
                minute: "2-digit",
                second: "2-digit"
              });
            }
          },
          min: chartTimeRange.start,
          max: chartTimeRange.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "Ping (ms)",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: 0,
          suggestedMax: Math.max(badThreshold + 50, maxPing + 50)
        }
      }
    }),
    [themeColors, avgPing, chartTimeRange, maxPing]
  );

  const charts: any = (
    <ChartErrorBoundary>
      <div style={{width: "100%", height: chartHeight * 1.5, position: "relative"}}>
        <Scatter ref={chartRef} data={data} options={options} />
      </div>
    </ChartErrorBoundary>
  );

  const dateStyle: any = {
    fontSize: 12,
    color: "#AAA",
    borderWidth: 1,
    borderColor: "#F4F4F4"
  };

  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <PageHeader
            title="Network Ping Test"
            subtitle="Measure your connection quality to the backend server region"
            icon="fa fa-wifi"
          />
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <div>
                    <Row>
                      <Col md="12">
                        <span style={dateStyle}>{latestTime}</span>
                      </Col>
                    </Row>
                    <hr />
                    <Row>
                      <Col md="10">{charts}</Col>
                      <Col md="2">
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>Max</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td>
                                <div className={`numbers ${maxPing > badThreshold ? "bad" : "good"}`}>{maxPing}</div>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>Avg</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td>
                                <div className={`numbers ${avgPing > badThreshold ? "bad" : "good"}`}>{avgPing}</div>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </div>

                  <div className="text-center mb-4">
                    <h4>Need help understanding latency and ping test results?</h4>
                    <HelpIcon topicId="pingTestLatency" size="lg" className="mr-2" />
                    <span className="text-muted">
                      &nbsp;Click for detailed information about latency and troubleshooting
                    </span>
                  </div>

                  <p>
                    <strong>Important note:</strong> This test measures your connection to servers near the virtual
                    cycling platform's infrastructure in Oregon, but it's not identical to how the virtual cycling
                    platform actually communicates with your device. The results give you a good indication of your
                    network quality, but your actual in-game experience may vary slightly.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

const PingTest = (props: any) => {
  return (
    <ChartThemeProvider>
      <PingTestContent {...props} />
    </ChartThemeProvider>
  );
};

export default PingTest;
